import { Linking, Text, View, type StyleProp, type ViewStyle } from 'react-native'
import { Image as ExpoImage } from 'expo-image'
import { Headphones, PhoneCall } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { COLORS, FONTS } from '@/lib/brand'
import { RADIUS, SPACING, INTERACTION, useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { toast } from '@/lib/toast'
import { Pressable } from '@/components/ui/Pressable'
import {
  COVERAGE_LIVE_PARTNER,
  getCoverageAssistancePhones,
  isCoveragePartnerBrandingVisible,
} from '@/lib/config/insurance-partners'

const QUALITAS_LOGO = require('../../assets/images/insurer-qualitas-logo.png')

interface CoverageAssistanceCardProps {
  compact?: boolean
  style?: StyleProp<ViewStyle>
  testID?: string
}

function coverageAssistancePhoneHref(phone: string) {
  const normalized = phone.replace(/[^\d+]/g, '')
  const withCountryCode = normalized.startsWith('+') ? normalized : `+503${normalized}`
  return `tel:${withCountryCode}`
}

async function openCoverageAssistanceDialer(phone: string, fallbackMessage: string) {
  try {
    await Linking.openURL(coverageAssistancePhoneHref(phone))
  } catch {
    toast.error(fallbackMessage)
  }
}

export function CoverageAssistanceCard({
  compact = false,
  style,
  testID = 'coverage.assistance-card',
}: CoverageAssistanceCardProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const phones = getCoverageAssistancePhones()
  const showOfficialBranding = isCoveragePartnerBrandingVisible()

  if (phones.length === 0) {
    return null
  }

  const primaryPhone = phones[0]
  if (!primaryPhone) return null

  const secondaryPhones = phones.slice(1)

  return (
    <View
      testID={testID}
      style={[
        {
          padding: compact ? SPACING.sm : SPACING.md,
          borderRadius: RADIUS.lg,
          borderWidth: 1,
          borderColor: colors.border,
          backgroundColor: colors.surfaceRaised,
          gap: compact ? SPACING.sm : SPACING.md,
        },
        style,
      ]}
      accessibilityRole="summary"
      accessibilityLabel={t('insurance.assistance_card_a11y')}
    >
      {/* Title and badge share the row; the body copy sits UNDER it at full
          card width. It used to live in the same flex column as the title, so
          the badge took the right half of the row and the sentence was cut
          mid-clause ("…La cobertura …") by a 2-line clamp. */}
      <View style={{ gap: SPACING.xs }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
          {showOfficialBranding ? (
            <View
              style={{
                flex: 1,
                minWidth: 0,
                height: compact ? 34 : 42,
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}
            >
              <ExpoImage
                source={QUALITAS_LOGO}
                style={{ width: compact ? 118 : 142, height: compact ? 34 : 42 }}
                contentFit="contain"
                accessibilityLabel={COVERAGE_LIVE_PARTNER.legalName}
              />
            </View>
          ) : (
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                numberOfLines={1}
                style={{
                  fontSize: compact ? 15 : 17,
                  lineHeight: compact ? 19 : 22,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                }}
              >
                {t('insurance.assistance_generic_title')}
              </Text>
            </View>
          )}

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              paddingHorizontal: SPACING.sm,
              paddingVertical: 6,
              borderRadius: RADIUS.full,
              backgroundColor: isDark ? 'rgba(19, 160, 176, 0.16)' : 'rgba(19, 160, 176, 0.10)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(19, 160, 176, 0.34)' : 'rgba(19, 160, 176, 0.20)',
            }}
          >
            <Headphones size={14} color="#139FB0" strokeWidth={2.2} />
            <Text
              style={{
                fontSize: 12,
                lineHeight: 15,
                fontFamily: FONTS.bodyBold,
                // #087F8E is 4.27:1 on its own 10% teal tint — the badge and its
                // fill are the same hue, so the tint eats most of the margin.
                // #0A7080 clears AA at 5.20:1 and stays the same teal.
                color: isDark ? '#72D7E2' : '#0A7080',
              }}
            >
              {showOfficialBranding ? '24/7' : t('insurance.assistance_generic_badge')}
            </Text>
          </View>
        </View>

        {!compact && !showOfficialBranding && (
          <Text
            style={{
              fontSize: 12,
              lineHeight: 17,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('insurance.assistance_generic_desc')}
          </Text>
        )}
      </View>

      {!compact && showOfficialBranding && (
        <View style={{ gap: 3 }}>
          <Text
            style={{
              fontSize: 16,
              lineHeight: 21,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
          >
            {t('insurance.assistance_card_title')}
          </Text>
          <Text
            style={{
              fontSize: 13,
              lineHeight: 18,
              fontFamily: FONTS.body,
              color: colors.textPrimary,
            }}
          >
            {t('insurance.assistance_card_desc')}
          </Text>
        </View>
      )}

      <View style={{ gap: SPACING.sm }}>
        <Pressable
          onPress={() =>
            openCoverageAssistanceDialer(primaryPhone, t('insurance.assistance_call_failed'))
          }
          testID={`${testID}.call-primary`}
          accessibilityRole="button"
          accessibilityLabel={t('insurance.assistance_call_primary', { phone: primaryPhone })}
          hitSlop={8}
          style={({ pressed }) => ({
            minHeight: compact ? 48 : 52,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm,
            paddingHorizontal: SPACING.md,
            paddingVertical: compact ? 10 : 12,
            borderRadius: RADIUS.full,
            // White sits on this fill, so the resting shade has to be the
            // one white can sit on; press feedback moves to opacity.
            backgroundColor: COLORS.coral.dark,
            opacity: pressed ? 0.85 : 1,
          })}
        >
          <PhoneCall size={17} color="#FFFFFF" strokeWidth={2.3} />
          <View style={{ minWidth: 0, alignItems: 'center' }}>
            <Text
              numberOfLines={1}
              style={{
                fontSize: 15,
                lineHeight: 19,
                fontFamily: FONTS.bodyBold,
                color: '#FFFFFF',
              }}
            >
              {t('insurance.assistance_call_now')}
            </Text>
            {!compact && (
              <Text
                numberOfLines={1}
                style={{
                  fontSize: 12,
                  lineHeight: 16,
                  fontFamily: FONTS.body,
                  // 0.82 over coral.dark is 4.13:1; 0.9 clears AA and still
                  // reads as the quieter half of the button's two lines.
                  color: 'rgba(255,255,255,0.9)',
                }}
              >
                {primaryPhone}
              </Text>
            )}
          </View>
        </Pressable>

        {secondaryPhones.length > 0 && (
          <View style={{ flexDirection: 'row', gap: SPACING.sm, flexWrap: 'wrap' }}>
            {secondaryPhones.map((phone, index) => (
              <Pressable
                key={phone}
                onPress={() =>
                  openCoverageAssistanceDialer(phone, t('insurance.assistance_call_failed'))
                }
                testID={`${testID}.call-secondary-${index + 1}`}
                accessibilityRole="button"
                accessibilityLabel={t('insurance.assistance_call_secondary', { phone })}
                hitSlop={6}
                style={({ pressed }) => ({
                  minHeight: INTERACTION.minTouchTarget,
                  flexGrow: 1,
                  flexBasis: compact ? 128 : 148,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.xs,
                  paddingHorizontal: SPACING.md,
                  paddingVertical: 9,
                  borderRadius: RADIUS.full,
                  backgroundColor: pressed
                    ? isDark
                      ? 'rgba(240, 66, 45, 0.16)'
                      : 'rgba(240, 66, 45, 0.08)'
                    : colors.surface,
                  borderWidth: 1,
                  // `colors.border` is #E5E5EA on this card's #FFFFFF fill in
                  // light — 1.15:1 — so the second way to reach assistance read
                  // as a line of text, not a control. Coral is the same family
                  // as the phone glyph inside it and clears the 3:1 minimum for
                  // a UI boundary in both themes (3.8:1 on #FFFFFF, 4.1:1 on
                  // #2C2947). Press feedback stays the tinted fill.
                  borderColor: COLORS.coral.DEFAULT,
                })}
              >
                <PhoneCall size={14} color={COLORS.coral.DEFAULT} strokeWidth={2.1} />
                <Text
                  numberOfLines={1}
                  style={{
                    fontSize: 13,
                    lineHeight: 17,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                >
                  {t('insurance.assistance_secondary_short', { phone })}
                </Text>
              </Pressable>
            ))}
          </View>
        )}

        {!compact && secondaryPhones.length === 0 && (
          <Text
            style={{
              fontSize: 12,
              lineHeight: 16,
              fontFamily: FONTS.body,
              color: colors.textMuted,
              textAlign: 'center',
            }}
          >
            {t('insurance.assistance_phone_ready', { phone: primaryPhone })}
          </Text>
        )}
      </View>
    </View>
  )
}
